import { ProductVariant } from '../../types/pz';
import { OrderProductLineDraft } from '../orders/orderProductLineTotals';
import { getProductSelectionLabel } from '../../utils/productSelectorLabel';
import type { QuantityUnit } from '../../utils/commercialQuantity';

interface FixedCustomerPrice {
  fixedPrice: number;
  currency: string;
}

interface OrderVariantLinePatch {
  productVariantId: string | null;
  name: string;
  unit: string;
  length?: number;
  width?: number;
  height?: number;
  thickness?: number;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  netWeight?: number;
  grossWeight?: number;
  cnCode?: string;
  countryOfOrigin?: string;
  unitPrice?: number;
  currency?: string;
}

const firstDefinedNumber = (...values: unknown[]) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

export function getVariantOrderLineName(variant?: ProductVariant) {
  if (!variant) return '';
  // Dimensions live in their dedicated order-line fields and codes/EAN remain
  // searchable in selectors. Keeping the stored name human-only prevents
  // technical identifiers and duplicated dimensions from leaking into
  // transports and warehouse PDFs downstream.
  return getProductSelectionLabel(variant, '');
}

export function getVariantOrderLinePatch(
  variantId: string | undefined,
  variant: ProductVariant | undefined,
  currentLine: OrderProductLineDraft,
  fixedPrice?: FixedCustomerPrice,
): OrderVariantLinePatch {
  const product = variant?.product;
  const defaultSalePrice = firstDefinedNumber(variant?.defaultSalePrice);
  const defaultSaleCurrency = variant?.defaultSaleCurrency;
  // The transaction owns its unit. A blank new line starts at pieces only as
  // a UI default; catalog metadata can never pin or rewrite that choice.
  const suggestedUnit = currentLine.unit || 'pcs';

  return {
    productVariantId: variantId || null,
    // The catalog picker is the single source of product identity. `name`
    // remains a persisted snapshot for historical documents, but is no longer
    // an independently edited label competing with the chosen product.
    name: getVariantOrderLineName(variant),
    unit: suggestedUnit as QuantityUnit,
    length: firstDefinedNumber(product?.length, variant?.length),
    width: firstDefinedNumber(product?.width, variant?.width),
    height: firstDefinedNumber(product?.height, variant?.height),
    thickness: firstDefinedNumber(product?.thickness, variant?.thickness),
    productCategoryName: product?.productCategoryName ?? null,
    productCategoryColor: product?.productCategoryColor ?? null,
    productCategoryDimensionDisplayProfile: product?.productCategoryDimensionDisplayProfile ?? null,
    netWeight: firstDefinedNumber(variant?.netWeight, product?.netWeight, currentLine.netWeight),
    grossWeight: firstDefinedNumber(variant?.grossWeight, product?.grossWeight, currentLine.grossWeight),
    cnCode: currentLine.cnCode || product?.cnCode,
    countryOfOrigin: currentLine.countryOfOrigin || product?.countryOfOrigin,
    ...(fixedPrice
      ? { unitPrice: fixedPrice.fixedPrice, currency: fixedPrice.currency }
      : defaultSalePrice !== undefined
        ? { unitPrice: defaultSalePrice, currency: defaultSaleCurrency || currentLine.currency }
        : {}),
  };
}
